import gql from 'graphql-tag'

export const BUSINESS_SUBSCRIPTION_QUERY = gql`
  query BusinessSubscription {
    currentUser {
      id
      isAdmin
      organizationMemberships {
        role
        organization {
          id
          name
          hasActiveSubscription
          networkSites {
            id
            name
          }
          socialAccounts {
            id
            platform
            status
          }
          businessSubscription {
            id
            status
            trialActive
            trialDaysRemaining
            stripePriceId
            planTier
            billingInterval
            canUpgradeToSocial
            brandingSettings
            contentFeed {
              id
              databaseId
              title
              websiteUrl
              enableSocialMedia
            }
          }
        }
      }
    }
  }
`

export const UPDATE_BRANDING_MUTATION = gql`
  mutation BusinessSubscriptionUpdateBranding(
    $organizationId: ID!
    $primaryColor: String
    $headingFont: String
    $bodyFont: String
    $showCategoryFilters: Boolean
    $calendarPageUrl: String
    $websiteUrl: String
  ) {
    businessSubscriptionUpdateBranding(
      organizationId: $organizationId
      primaryColor: $primaryColor
      headingFont: $headingFont
      bodyFont: $bodyFont
      showCategoryFilters: $showCategoryFilters
      calendarPageUrl: $calendarPageUrl
      websiteUrl: $websiteUrl
    ) {
      businessSubscription {
        id
        brandingSettings
      }
      detectedColors
      extractionMessage
      errors {
        attribute
        message
      }
    }
  }
`

export const BILLING_PORTAL_MUTATION = gql`
  mutation BusinessSubscriptionBillingPortal($organizationId: ID!) {
    businessSubscriptionBillingPortal(organizationId: $organizationId) {
      portalUrl
      errors {
        attribute
        message
      }
    }
  }
`

export const UPGRADE_TO_SOCIAL_MUTATION = gql`
  mutation BusinessSubscriptionUpgrade($organizationId: ID!) {
    businessSubscriptionUpgrade(organizationId: $organizationId) {
      businessSubscription {
        id
        stripePriceId
        planTier
        billingInterval
      }
      errors {
        attribute
        message
      }
    }
  }
`

export type UpgradeToSocialResponse = {
  businessSubscriptionUpgrade: {
    businessSubscription: {
      id: string
      stripePriceId: string
      planTier: string
      billingInterval: string
    } | null
    errors: Array<{ attribute: string | null; message: string }> | null
  }
}

export type BrandingSettings = {
  primaryColor?: string
  headingFont?: string
  bodyFont?: string
  showCategoryFilters?: boolean
}

// Note: headingFont and bodyFont are already part of this type and
// are accepted by the UPDATE_BRANDING_MUTATION via the GraphQL args.

export type BusinessSubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'canceled'

// Human-readable label for a subscription status badge. The raw enum
// (e.g. "past_due") is never shown to owners.
const SUBSCRIPTION_STATUS_LABELS: Record<BusinessSubscriptionStatus, string> = {
  trialing: 'Trial',
  active: 'Active',
  past_due: 'Payment due',
  canceled: 'Canceled',
}

export function humanizeSubscriptionStatus(status: string): string {
  return SUBSCRIPTION_STATUS_LABELS[status as BusinessSubscriptionStatus] ?? status
}

export type BusinessSubscription = {
  id: string
  status: BusinessSubscriptionStatus
  trialActive: boolean
  trialDaysRemaining: number | null
  stripePriceId: string
  planTier: string
  billingInterval: string
  canUpgradeToSocial: boolean
  brandingSettings: BrandingSettings
  contentFeed: {
    id: string
    databaseId: number
    title: string
    websiteUrl: string | null
    enableSocialMedia: boolean
  } | null
}

export type NetworkSite = {
  id: number
  name: string
}

export type SocialAccountInfo = {
  id: string
  platform: string
  status: string
}

export type SubscriptionOrganization = {
  id: string
  name: string
  hasActiveSubscription: boolean
  networkSites: NetworkSite[]
  socialAccounts: SocialAccountInfo[]
  businessSubscription: BusinessSubscription | null
}

export type BusinessSubscriptionQueryResponse = {
  currentUser: {
    id: string
    isAdmin: boolean
    organizationMemberships: Array<{
      role: string
      organization: SubscriptionOrganization
    }>
  }
}

export type UpdateBrandingResponse = {
  businessSubscriptionUpdateBranding: {
    businessSubscription: {
      id: string
      brandingSettings: BrandingSettings
    } | null
    detectedColors: string[] | null
    extractionMessage: string | null
    errors: Array<{ attribute: string | null; message: string }> | null
  }
}

export type BillingPortalResponse = {
  businessSubscriptionBillingPortal: {
    portalUrl: string | null
    errors: Array<{ attribute: string | null; message: string }> | null
  }
}

export const BUSINESS_CALENDAR_ANALYTICS_QUERY = gql`
  query BusinessCalendarAnalytics($organizationId: ID!, $startDate: String, $endDate: String) {
    businessCalendarAnalytics(organizationId: $organizationId, startDate: $startDate, endDate: $endDate) {
      summary {
        siteImpressions
        networkImpressions
        siteClicks
        networkClicks
        sitePageViews
        networkPageViews
        totalImpressions
        totalClicks
        totalOutboundClicks
      }
      dailyStats {
        date
        impressions
        clicks
        outboundClicks
      }
      topEvents {
        eventId
        title
        slug
        views
        siteViews
        networkViews
        siteClicks
        networkClicks
        startAt
        timeZone
        dailyViews {
          date
          siteViews
          networkViews
        }
      }
    }
  }
`

export type AnalyticsSummary = {
  siteImpressions: number
  networkImpressions: number
  siteClicks: number
  networkClicks: number
  sitePageViews: number
  networkPageViews: number
  totalImpressions: number
  totalClicks: number
  totalOutboundClicks: number
}

export type DailyStat = {
  date: string
  impressions: number
  clicks: number
  outboundClicks: number
}

export type EventDailyView = {
  date: string
  siteViews: number
  networkViews: number
}

export type TopEvent = {
  eventId: string
  title: string
  slug: string | null
  views: number
  siteViews: number
  networkViews: number
  siteClicks: number
  networkClicks: number
  dailyViews: EventDailyView[]
  startAt: string | null
  timeZone: string | null
}

export type BusinessCalendarAnalyticsResponse = {
  businessCalendarAnalytics: {
    summary: AnalyticsSummary
    dailyStats: DailyStat[]
    topEvents: TopEvent[]
  } | null
}
